/**
 * /admin/leads — Facebook-Lead-Dashboard.
 *
 * Beantwortet die Frage, die beim Optimieren zählt: Welche Anzeige bringt Leute,
 * die ans Telefon gehen und am Ende einen Bildungsgutschein bekommen, und was
 * kostet das. Dafür wird die Kette Anzeige → Besucher → Lead → Pipedrive-Stand →
 * Ausgaben je Kampagne/Anzeige zusammengeführt.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardPasswordGate from '../analytics-dashboard/components/DashboardPasswordGate';
import { isAuthenticated } from '../../services/apiService';

const C = {
  brand: '#3E4954', text: '#1e2b38', muted: '#6b7a8d', faint: '#a3adb9',
  border: '#dde1e7', borderSm: '#eef0f3', bg: '#f5f6f8', white: '#fff',
  green: '#16a34a', greenBg: '#dcfce7', red: '#dc2626', redBg: '#fee2e2',
  amber: '#d97706', amberBg: '#fef3c7', blue: '#1D4ED8', blueBg: '#eff6ff',
  violet: '#6d28d9', violetBg: '#ede9fe',
};

const CSS = `
.ld { min-height: 100vh; background: ${C.bg}; font-family: var(--font-ui, 'Outfit', system-ui, sans-serif); color: ${C.text}; }
.ld * { box-sizing: border-box; }
.ld-head { background: ${C.white}; border-bottom: 1px solid ${C.border}; min-height: 56px; display: flex; align-items: center;
  justify-content: space-between; padding: 8px 20px; position: sticky; top: 0; z-index: 30; gap: 12px; flex-wrap: wrap; }
.ld-wrap { max-width: 1560px; margin: 0 auto; padding: 18px 20px 60px; }
.ld-card { background: ${C.white}; border: 1px solid ${C.border}; border-radius: 16px; }
.ld-title { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: ${C.muted};
  padding: 13px 16px 9px; border-bottom: 1px solid ${C.borderSm}; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.ld-title small { font-weight: 500; text-transform: none; letter-spacing: 0; color: ${C.faint}; }

.ld-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(128px, 1fr)); gap: 10px; margin-bottom: 14px; }
.ld-kpi { background: ${C.white}; border: 1px solid ${C.border}; border-radius: 14px; padding: 13px 15px; }
.ld-kpi .v { font-size: 23px; font-weight: 700; line-height: 1.1; letter-spacing: -.02em; }
.ld-kpi .k { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: ${C.muted}; margin-top: 6px; }
.ld-kpi .s { font-size: 11px; color: ${C.faint}; margin-top: 3px; }

.ld-grid { display: grid; gap: 12px; margin-bottom: 14px; align-items: start; }
.ld-grid.c3 { grid-template-columns: 1.2fr 1fr 1fr; }
@media (max-width: 1100px) { .ld-grid.c3 { grid-template-columns: 1fr 1fr; } }
@media (max-width: 720px)  { .ld-grid.c3 { grid-template-columns: 1fr; } }

.ld-funnel { padding: 12px 16px 14px; }
.ld-frow { margin-bottom: 10px; }
.ld-fmeta { display: flex; justify-content: space-between; align-items: baseline; font-size: 12.5px; margin-bottom: 4px; }
.ld-ftrack { height: 9px; background: ${C.borderSm}; border-radius: 100px; overflow: hidden; }
.ld-ftrack > i { display: block; height: 100%; background: linear-gradient(90deg, ${C.brand}, #5b6b7c); border-radius: 100px; }
.ld-drop { font-size: 11px; color: ${C.red}; font-weight: 700; margin-left: 6px; }

.ld-bars { padding: 10px 16px 12px; }
.ld-bar { display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; font-size: 12.5px; margin-bottom: 7px; }
.ld-bar .lbl span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; }
.ld-bar .track { height: 6px; background: ${C.borderSm}; border-radius: 100px; overflow: hidden; margin-top: 3px; }
.ld-bar .track > i { display: block; height: 100%; background: ${C.brand}; border-radius: 100px; }
.ld-bar .n { font-weight: 700; font-variant-numeric: tabular-nums; color: ${C.muted}; white-space: nowrap; }

.ld-spark { display: flex; align-items: flex-end; gap: 2px; height: 58px; padding: 12px 16px 8px; }
.ld-spark div { flex: 1; background: ${C.brand}; border-radius: 2px 2px 0 0; min-height: 2px; opacity: .85; }

.ld-heat { padding: 10px 12px 12px; overflow-x: auto; }
.ld-heat table { border-collapse: separate; border-spacing: 2px; }
.ld-heat th { font-size: 9.5px; font-weight: 600; color: ${C.faint}; padding: 0 2px; text-align: center; }
.ld-heat th.wd { text-align: right; padding-right: 6px; color: ${C.muted}; font-weight: 700; }
.ld-heat td { width: 15px; height: 15px; border-radius: 3px; background: ${C.borderSm}; }

table.ld-t { width: 100%; border-collapse: collapse; }
.ld-t th { text-align: left; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  color: ${C.muted}; padding: 9px 10px; border-bottom: 1px solid ${C.border}; background: #fafbfc; white-space: nowrap; }
.ld-t th.r, .ld-t td.r { text-align: right; font-variant-numeric: tabular-nums; }
.ld-t td { padding: 10px 10px; border-bottom: 1px solid ${C.borderSm}; font-size: 12.5px; vertical-align: middle; }
.ld-t tbody tr { cursor: pointer; }
.ld-t tbody tr:hover { background: #fafbfc; }
.ld-t tbody tr.sel { background: ${C.blueBg}; }
.ld-t tfoot td { font-weight: 700; background: #fafbfc; border-top: 2px solid ${C.border}; }
.mono { font-family: 'JetBrains Mono', Menlo, Consolas, monospace; font-size: 11.5px; }

.pill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 100px; font-size: 10.5px; font-weight: 700; white-space: nowrap; }
.dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; flex-shrink: 0; }

.ld-input { height: 34px; padding: 0 11px; border: 1.5px solid ${C.border}; border-radius: 9px; font: inherit; font-size: 13px; color: ${C.text}; background: ${C.white}; outline: none; }
.ld-input:focus { border-color: ${C.brand}; }
.ld-btn { height: 34px; padding: 0 13px; border-radius: 9px; border: 1.5px solid ${C.border}; background: ${C.white}; color: ${C.brand};
  font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; white-space: nowrap; }
.ld-btn:hover { border-color: ${C.brand}; }
.ld-btn.primary { background: ${C.brand}; color: ${C.white}; border-color: ${C.brand}; }
.ld-btn:disabled { opacity: .5; cursor: default; }

.ld-drawer { position: fixed; top: 0; right: 0; bottom: 0; width: 460px; max-width: 94vw; background: ${C.white}; border-left: 1px solid ${C.border};
  box-shadow: -12px 0 40px rgba(30,43,56,.16); z-index: 50; overflow-y: auto; animation: ld-slide 220ms cubic-bezier(.22,1,.36,1) both; }
@keyframes ld-slide { from { transform: translateX(28px); opacity: 0; } to { transform: none; opacity: 1; } }
.ld-scrim { position: fixed; inset: 0; background: rgba(30,43,56,.28); z-index: 49; }
.ld-sec { padding: 14px 18px; border-bottom: 1px solid ${C.borderSm}; }
.ld-sec h4 { margin: 0 0 10px; font-size: 10.5px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: ${C.muted}; }
.ld-pair { display: grid; grid-template-columns: 124px 1fr; gap: 8px; padding: 4px 0; font-size: 13px; }
.ld-pair .k { color: ${C.muted}; }
.ld-pair .v { color: ${C.text}; font-weight: 600; word-break: break-word; }
.ld-empty { padding: 40px 20px; text-align: center; color: ${C.muted}; font-size: 14px; }
.ld-hint { padding: 12px 16px; font-size: 12.5px; line-height: 1.55; color: ${C.muted}; }
.ld-hint code { font-family: 'JetBrains Mono', Menlo, monospace; font-size: 11.5px; background: ${C.borderSm}; padding: 1px 5px; border-radius: 4px; color: ${C.text}; }
`;

const REACH = { vormittags: 'Vormittags 8–12', nachmittags: 'Nachmittags 12–16', abends: 'Abends 16–18', egal: 'Jederzeit' };
const ARBEIT = { ja: 'Arbeitssuchend', bald: 'Bald arbeitssuchend', nein: 'Berufstätig' };
const STATUS_STYLE = {
  ok: { bg: C.greenBg, color: C.green, label: 'OK' },
  error: { bg: C.redBg, color: C.red, label: 'Fehler' },
  skipped: { bg: C.amberBg, color: C.amber, label: 'Übersprungen' },
  unknown: { bg: C.borderSm, color: C.muted, label: 'Offen' },
};
const OUTCOME_STYLE = {
  neu:            { bg: C.borderSm, color: C.muted },
  nicht_erreicht: { bg: C.amberBg, color: C.amber },
  im_gespraech:   { bg: C.blueBg, color: C.blue },
  im_antrag:      { bg: C.violetBg, color: C.violet },
  zusage:         { bg: C.greenBg, color: C.green },
  verloren:       { bg: C.redBg, color: C.red },
  kein_deal:      { bg: C.borderSm, color: C.faint },
  spam:           { bg: C.redBg, color: C.red },
};
const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const LANG_NAMES = { de: 'Deutsch', en: 'Englisch', tr: 'Türkisch', ru: 'Russisch', uk: 'Ukrainisch', ar: 'Arabisch', pl: 'Polnisch', ro: 'Rumänisch', bg: 'Bulgarisch', fa: 'Persisch', it: 'Italienisch', es: 'Spanisch', fr: 'Französisch', sq: 'Albanisch', sr: 'Serbisch', hr: 'Kroatisch', ku: 'Kurdisch' };

function fmtDate(ts) {
  if (!ts) return '–';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  return isNaN(d) ? ts : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtTime(ms) { return ms ? new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '–'; }
function fmtDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return '–';
  return sec < 60 ? `${sec} s` : `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')} min`;
}
function fmtEur(v) { return v === null || v === undefined ? '–' : v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: v >= 100 ? 0 : 2 }); }
function pct(a, b) { return b ? `${Math.round((a / b) * 100)} %` : '–'; }
function deviceIcon(t) { return t === 'mobile' ? '📱' : t === 'tablet' ? '📟' : t === 'desktop' ? '🖥' : '❔'; }

function StatusPill({ status, title }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  return <span className="pill" style={{ background: s.bg, color: s.color }} title={title || ''}><span className="dot" style={{ background: s.color }} />{s.label}</span>;
}
function OutcomePill({ outcome, label, stage }) {
  const s = OUTCOME_STYLE[outcome] || OUTCOME_STYLE.neu;
  return <span className="pill" style={{ background: s.bg, color: s.color }} title={stage || ''}>{label}</span>;
}
function Pair({ k, v, mono }) {
  if (v === null || v === undefined || v === '') return null;
  return <div className="ld-pair"><span className="k">{k}</span><span className={`v${mono ? ' mono' : ''}`}>{v}</span></div>;
}
function BarList({ items, total, empty = 'Noch keine Daten' }) {
  if (!items?.length || items.every(i => i.name === '—')) return <div style={{ padding: '2px 0 6px', fontSize: 12.5, color: C.faint }}>{empty}</div>;
  const max = Math.max(...items.map(i => i.count), 1);
  return items.map(i => (
    <div className="ld-bar" key={i.name}>
      <div style={{ minWidth: 0 }}>
        <div className="lbl"><span>{i.name}</span></div>
        <div className="track"><i style={{ width: `${(i.count / max) * 100}%` }} /></div>
      </div>
      <span className="n">{i.count}{total ? ` · ${Math.round((i.count / total) * 100)} %` : ''}</span>
    </div>
  ));
}
function Funnel({ steps, note }) {
  return (
    <div className="ld-funnel">
      {steps.map((f, i) => {
        const prev = i > 0 ? steps[i - 1].count : null;
        const drop = prev ? Math.round((1 - f.count / prev) * 100) : 0;
        return (
          <div className="ld-frow" key={f.key}>
            <div className="ld-fmeta">
              <span>{f.label}</span>
              <span><b>{f.count}</b> <span style={{ color: C.faint }}>({f.pct} %)</span>{drop > 0 && <span className="ld-drop">−{drop} %</span>}</span>
            </div>
            <div className="ld-ftrack"><i style={{ width: `${f.pct}%` }} /></div>
          </div>
        );
      })}
      {note && <div style={{ fontSize: 11.5, color: C.faint, marginTop: 8, lineHeight: 1.5 }}>{note}</div>}
    </div>
  );
}

export default function LeadDashboard() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [fOutcome, setFOutcome] = useState('');
  const [fCampaign, setFCampaign] = useState('');
  const [selected, setSelected] = useState(null);
  const [updated, setUpdated] = useState('');

  const load = useCallback(async (refresh = false) => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`/api/admin/leads?days=${days}${refresh ? '&refresh=1' : ''}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` },
      });
      if (res.status === 401) { setAuthed(false); return; }
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Laden fehlgeschlagen');
      setData(json);
      setUpdated(new Date().toLocaleTimeString('de-DE'));
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const leads = data?.leads || [];
  const campKey = l => [l.utm_source || '—', l.utm_campaign || '—', l.utm_content || '—'].join('||');
  const filtered = useMemo(() => leads.filter(l => {
    const q = search.trim().toLowerCase();
    const hit = !q || [l.vorname, l.nachname, l.email, l.telefon, l.ort, l.booking_ref, l.utm_campaign, l.utm_content, l.geo_city, l.pd_stage]
      .some(v => (v || '').toLowerCase().includes(q));
    return hit && (!fOutcome || l.outcome === fOutcome) && (!fCampaign || campKey(l) === fCampaign);
  }), [leads, search, fOutcome, fCampaign]);

  const exportCsv = () => {
    const cols = ['booking_ref','created_at','vorname','nachname','telefon','email','ort','arbeitslos','fuehrerschein_b','erreichbarkeit',
      'utm_source','utm_medium','utm_campaign','utm_content','utm_term','placement','fb_campaign_id','fb_adset_id','fb_ad_id',
      'device_type','os','browser','is_inapp','browser_lang','geo_country','geo_region','geo_city','time_to_submit',
      'outcome_label','pd_stage','pd_deal_url','meta_status','oai_status','pd_status','mail_status'];
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.join(';'), ...filtered.map(l => cols.map(c => esc(l[c])).join(';'))].join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `zba-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  if (!authed) return <><style>{CSS}</style><DashboardPasswordGate onSuccess={() => setAuthed(true)} /></>;

  const k = data?.kpis;
  const intg = data?.integrations;
  const spendOn = !!intg?.meta_insights?.configured;
  const pdOn = !!intg?.pipedrive?.configured;
  const funnelSteps = (fCampaign && data?.funnelByCampaign?.[fCampaign]) || data?.funnel || [];
  const selCamp = fCampaign ? data?.campaigns?.find(c => c.key === fCampaign) : null;
  const heatMax = Math.max(1, ...(data?.heatmap || []).flat());
  const maxDay = Math.max(1, ...(data?.timeline || []).map(t => t.count));
  const sumCol = f => (data?.campaigns || []).reduce((a, c) => a + (c[f] || 0), 0);

  return (
    <div className="ld">
      <style>{CSS}</style>

      <div className="ld-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexWrap: 'wrap' }}>
          <img src="https://zba.academy/webinar/images/zbalogo2.png" alt="ZBA" style={{ height: 22 }} />
          <strong style={{ fontSize: 14 }}>Facebook-Leads</strong>
          {updated && <span style={{ fontSize: 11, color: C.faint }}>Stand {updated}</span>}
          {intg && (
            <span style={{ display: 'flex', gap: 6 }}>
              <span className="pill" style={{ background: pdOn ? C.greenBg : C.amberBg, color: pdOn ? C.green : C.amber }} title={pdOn ? `Pipedrive-Stände von ${fmtTime(intg.pipedrive.cachedAt)}` : 'Pipedrive nicht konfiguriert'}>
                Pipedrive {pdOn ? fmtTime(intg.pipedrive.cachedAt) : 'aus'}
              </span>
              <span className="pill" style={{ background: spendOn ? C.greenBg : C.borderSm, color: spendOn ? C.green : C.muted }} title={intg.meta_insights.error || ''}>
                Meta-Ausgaben {spendOn ? (intg.meta_insights.error ? 'Fehler' : fmtTime(intg.meta_insights.cachedAt)) : 'nicht verbunden'}
              </span>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select className="ld-input" value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>7 Tage</option><option value={14}>14 Tage</option><option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option><option value={365}>1 Jahr</option>
          </select>
          <button className="ld-btn" onClick={exportCsv} disabled={!filtered.length}>⬇ CSV</button>
          <a className="ld-btn" href="/admin/bookings">📋 Buchungen</a>
          <a className="ld-btn" href="/crm">📊 CRM</a>
          <button className="ld-btn" onClick={() => load(true)} disabled={loading} title="Pipedrive und Meta neu abfragen">⟳ Neu abfragen</button>
          <button className="ld-btn primary" onClick={() => load(false)} disabled={loading}>{loading ? 'Lädt…' : 'Aktualisieren'}</button>
        </div>
      </div>

      <div className="ld-wrap">
        {err && <div className="ld-card" style={{ padding: 14, marginBottom: 14, color: C.red, fontWeight: 600 }}>{err}</div>}
        {!data && loading && <div className="ld-empty">Lade Leads, Pipedrive-Stände und Ausgaben…</div>}

        {data && (
          <>
            <div className="ld-kpis">
              <div className="ld-kpi"><div className="v">{k.visitors}</div><div className="k">Besucher</div><div className="s">Seite geöffnet</div></div>
              <div className="ld-kpi"><div className="v">{k.total}</div><div className="k">Leads</div><div className="s">{k.today} heute · {k.last7} in 7 Tagen</div></div>
              <div className="ld-kpi"><div className="v">{k.cr === null ? '–' : `${k.cr} %`}</div><div className="k">Conversion</div><div className="s">Besucher → Lead</div></div>
              <div className="ld-kpi"><div className="v">{k.qualified}</div><div className="k">Qualifiziert</div><div className="s">{pct(k.qualified, k.total)} · förderfähig + FS</div></div>
              <div className="ld-kpi"><div className="v" style={{ color: k.reached ? C.green : C.text }}>{k.reached}</div><div className="k">Erreicht</div><div className="s">{pct(k.reached, k.with_deal)} der Deals · {k.nicht_erreicht} verpasst</div></div>
              <div className="ld-kpi"><div className="v" style={{ color: C.violet }}>{k.im_antrag}</div><div className="k">Im Antrag</div><div className="s">Bildungsgutschein läuft</div></div>
              <div className="ld-kpi"><div className="v" style={{ color: C.green }}>{k.zusage}</div><div className="k">Zusage / BGS</div><div className="s">{k.verloren} verloren</div></div>
              {spendOn ? (
                <>
                  <div className="ld-kpi"><div className="v">{fmtEur(k.spend)}</div><div className="k">Ausgaben</div><div className="s">Meta, {data.days} Tage</div></div>
                  <div className="ld-kpi"><div className="v" style={{ color: C.blue }}>{fmtEur(k.cpl)}</div><div className="k">Kosten / Lead</div><div className="s">{fmtEur(k.cpql)} je qualifiziertem</div></div>
                </>
              ) : (
                <div className="ld-kpi"><div className="v">{fmtDuration(k.avg_seconds)}</div><div className="k">Ø Ausfüllzeit</div><div className="s">{k.inapp} aus FB/Insta-App</div></div>
              )}
              <div className="ld-kpi"><div className="v" style={{ color: (k.meta_problem || k.pd_problem || k.oai_problem) ? C.red : C.green }}>{k.meta_ok}/{k.oai_configured ? `${k.oai_ok}/` : ''}{k.pd_ok}</div><div className="k">Meta{k.oai_configured ? ' / GPT' : ''} / Pipedrive</div><div className="s">{k.meta_problem || k.pd_problem ? `${k.meta_problem + k.pd_problem} Übergaben mit Problem` : 'alle übertragen'} · {k.duplicates} Doppelte{k.suspicious ? ` · ${k.suspicious} Bot-Verdacht` : ''}</div></div>
            </div>

            <div className="ld-card" style={{ marginBottom: 14 }}>
              <div className="ld-title">
                <span>Anzeigen im Vergleich <small>Zeile anklicken filtert Trichter und Liste</small></span>
                {fCampaign && <button className="ld-btn" style={{ height: 26, fontSize: 12 }} onClick={() => setFCampaign('')}>Filter aufheben ✕</button>}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ld-t">
                  <thead><tr>
                    <th>Quelle</th><th>Kampagne</th><th>Anzeige</th>
                    <th className="r" title="Sitzungen, die Schritt 1 gesehen haben">Besucher</th>
                    <th className="r">Leads</th>
                    <th className="r" title="Leads je Besucher">CR</th>
                    <th className="r" title="arbeitssuchend oder bald + Führerschein B">Qualif.</th>
                    <th className="r" title="Pipedrive-Stand: Gespräch, Antrag oder Zusage">Erreicht</th>
                    <th className="r">Antrag</th>
                    <th className="r">Zusage</th>
                    {spendOn && <><th className="r">Ausgaben</th><th className="r" title="Kosten je Lead">CPL</th><th className="r" title="Kosten je qualifiziertem Lead">CPQL</th><th className="r" title="Kosten je erreichtem Lead">€/Erreicht</th></>}
                  </tr></thead>
                  <tbody>
                    {data.campaigns.length === 0 && <tr><td colSpan={14} style={{ color: C.faint }}>Noch keine Daten im Zeitraum</td></tr>}
                    {data.campaigns.map(c => (
                      <tr key={c.key} className={fCampaign === c.key ? 'sel' : ''} onClick={() => setFCampaign(fCampaign === c.key ? '' : c.key)}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {c.source === 'direkt' ? <span className="pill" style={{ background: C.borderSm, color: C.muted }}>direkt</span>
                            : c.metaOnly ? <span className="pill" style={{ background: C.amberBg, color: C.amber }} title="Meta meldet Ausgaben oder Klicks, aber es kam kein Besucher an — Anzeigen-Link prüfen">nur Meta</span>
                            : <b>{c.source}</b>}
                        </td>
                        <td style={{ fontWeight: 700, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.campaign}>{c.campaign}</td>
                        <td style={{ color: C.muted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.content}>{c.content}</td>
                        <td className="r">{c.visitors || '–'}</td>
                        <td className="r" style={{ fontWeight: 700 }}>{c.leads}</td>
                        <td className="r" style={{ color: c.cr === null ? C.faint : c.cr >= 10 ? C.green : c.cr < 4 ? C.red : C.text }}>{c.cr === null ? '–' : `${c.cr} %`}</td>
                        <td className="r">{c.qualified} <span style={{ color: C.faint }}>({pct(c.qualified, c.leads)})</span></td>
                        <td className="r" style={{ color: c.reached ? C.green : C.faint }}>{c.reached} <span style={{ color: C.faint }}>({pct(c.reached, c.leads)})</span></td>
                        <td className="r" style={{ color: c.im_antrag ? C.violet : C.faint }}>{c.im_antrag}</td>
                        <td className="r" style={{ color: c.zusage ? C.green : C.faint, fontWeight: c.zusage ? 700 : 400 }}>{c.zusage}</td>
                        {spendOn && <>
                          <td className="r">{fmtEur(c.spend)}</td>
                          <td className="r" style={{ fontWeight: 700 }}>{fmtEur(c.cpl)}</td>
                          <td className="r">{fmtEur(c.cpql)}</td>
                          <td className="r">{fmtEur(c.cp_reached)}</td>
                        </>}
                      </tr>
                    ))}
                  </tbody>
                  {data.campaigns.length > 1 && (
                    <tfoot><tr>
                      <td colSpan={3}>Gesamt</td>
                      <td className="r">{sumCol('visitors') || '–'}</td><td className="r">{sumCol('leads')}</td>
                      <td className="r">{k.cr === null ? '–' : `${k.cr} %`}</td>
                      <td className="r">{sumCol('qualified')}</td><td className="r">{sumCol('reached')}</td>
                      <td className="r">{sumCol('im_antrag')}</td><td className="r">{sumCol('zusage')}</td>
                      {spendOn && <><td className="r">{fmtEur(k.spend)}</td><td className="r">{fmtEur(k.cpl)}</td><td className="r">{fmtEur(k.cpql)}</td><td className="r">{k.reached ? fmtEur(Math.round((k.spend / k.reached) * 100) / 100) : '–'}</td></>}
                    </tr></tfoot>
                  )}
                </table>
              </div>
              {!spendOn && (
                <div className="ld-hint" style={{ borderTop: `1px solid ${C.borderSm}` }}>
                  <b>Kosten je Lead fehlen noch.</b> Trag in der <code>.env</code> die Werbekonto-ID ein (<code>META_AD_ACCOUNT_ID=act_…</code>) und gib dem
                  System-User des CAPI-Tokens im Business Manager die Berechtigung <code>ads_read</code> auf dieses Werbekonto. Dann erscheinen hier Ausgaben, CPL und Kosten je qualifiziertem Lead.
                  {intg?.meta_insights?.error && <div style={{ color: C.red, marginTop: 6 }}>Meta-Antwort: {intg.meta_insights.error}</div>}
                </div>
              )}
              {spendOn && intg?.meta_insights?.error && (
                <div className="ld-hint" style={{ borderTop: `1px solid ${C.borderSm}`, color: C.red }}>Meta-Ausgaben konnten nicht geladen werden: {intg.meta_insights.error}</div>
              )}
              {spendOn && !intg?.meta_insights?.error && (
                <div className="ld-hint" style={{ borderTop: `1px solid ${C.borderSm}` }}>
                  Meta hat für den Zeitraum <b>{intg.meta_insights.rows}</b> Anzeigen-Zeilen geliefert, Stand {fmtTime(intg.meta_insights.cachedAt)}.
                  {intg.meta_insights.rows === 0 && ' Ausgaben des laufenden Tages erscheinen bei Meta oft erst mit einigen Stunden Verzögerung.'}
                  {' '}Zeilen mit „nur Meta" haben Ausgaben oder Klicks, aber keinen Besucher auf der Seite — dort den Anzeigen-Link prüfen.
                </div>
              )}
            </div>

            <div className="ld-grid c3">
              <div className="ld-card">
                <div className="ld-title"><span>Formular-Trichter {selCamp && <small>· {selCamp.campaign} / {selCamp.content}</small>}</span></div>
                <Funnel steps={funnelSteps} note="Eindeutige Sitzungen je Schritt. Der größte Sprung zeigt, welche Frage die meisten kostet. Ohne Filter: alle Besucher." />
              </div>
              <div className="ld-card">
                <div className="ld-title"><span>Leads pro Tag</span></div>
                <div className="ld-spark">{data.timeline.slice(-45).map(t => <div key={t.date} style={{ height: `${(t.count / maxDay) * 100}%` }} title={`${t.date}: ${t.count}`} />)}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px 10px', fontSize: 11, color: C.faint }}><span>{data.timeline.slice(-45)[0]?.date}</span><span>heute</span></div>
                <div className="ld-title" style={{ borderTop: `1px solid ${C.borderSm}` }}><span>Wann kommen Leads rein <small>Wochentag × Uhrzeit</small></span></div>
                <div className="ld-heat">
                  <table>
                    <thead><tr><th /> {Array.from({ length: 24 }, (_, h) => <th key={h}>{h % 3 === 0 ? h : ''}</th>)}</tr></thead>
                    <tbody>{data.heatmap.map((row, wd) => (
                      <tr key={wd}><th className="wd">{WEEKDAYS[wd]}</th>{row.map((v, h) => (
                        <td key={h} title={`${WEEKDAYS[wd]} ${h}:00 – ${v} Lead${v === 1 ? '' : 's'}`}
                          style={{ background: v ? `rgba(62,73,84,${0.15 + 0.85 * (v / heatMax)})` : undefined }} />
                      ))}</tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
              <div className="ld-card">
                <div className="ld-title"><span>Was aus den Leads wurde <small>Pipedrive</small></span></div>
                <div className="ld-bars"><BarList items={data.breakdown.outcome} total={k.total} /></div>
                <div className="ld-title" style={{ borderTop: `1px solid ${C.borderSm}` }}><span>Beste Erreichbarkeit</span></div>
                <div className="ld-bars"><BarList items={data.breakdown.erreichbarkeit.map(i => ({ ...i, name: REACH[i.name] || i.name }))} total={k.total} /></div>
              </div>
            </div>

            <div className="ld-grid c3">
              <div className="ld-card">
                <div className="ld-title"><span>Quelle &amp; Platzierung</span></div>
                <div className="ld-bars"><BarList items={data.breakdown.source} total={k.total} /></div>
                <div className="ld-title" style={{ borderTop: `1px solid ${C.borderSm}` }}><span>Platzierung <small>Feed, Story, Reels …</small></span></div>
                <div className="ld-bars"><BarList items={data.breakdown.placement} total={k.total} empty="Platzierung kommt mit {{placement}} im Anzeigen-Link" /></div>
              </div>
              <div className="ld-card">
                <div className="ld-title"><span>Gerät &amp; Browser</span></div>
                <div className="ld-bars">
                  <BarList items={data.breakdown.device.map(i => ({ ...i, name: `${deviceIcon(i.name)} ${i.name}` }))} total={k.total} />
                  <div style={{ height: 8 }} />
                  <BarList items={data.breakdown.browser} total={k.total} />
                </div>
                <div className="ld-title" style={{ borderTop: `1px solid ${C.borderSm}` }}><span>Betriebssystem</span></div>
                <div className="ld-bars"><BarList items={data.breakdown.os} total={k.total} /></div>
              </div>
              <div className="ld-card">
                <div className="ld-title"><span>Browsersprache <small>Hinweis auf Muttersprache</small></span></div>
                <div className="ld-bars"><BarList items={data.breakdown.language.map(i => ({ ...i, name: LANG_NAMES[i.name] ? `${LANG_NAMES[i.name]} (${i.name})` : i.name }))} total={k.total} /></div>
                <div className="ld-title" style={{ borderTop: `1px solid ${C.borderSm}` }}><span>Ungefähre Herkunft <small>aus der IP, lokal</small></span></div>
                <div className="ld-bars"><BarList items={data.breakdown.city} total={k.total} empty="Wird ab dem nächsten Lead gefüllt" /></div>
              </div>
            </div>

            <div className="ld-card">
              <div style={{ display: 'flex', gap: 8, padding: 12, borderBottom: `1px solid ${C.borderSm}`, flexWrap: 'wrap' }}>
                <input className="ld-input" style={{ flex: 1, minWidth: 220 }} placeholder="Name, Telefon, E-Mail, Ort, Kampagne oder Pipedrive-Stage…" value={search} onChange={e => setSearch(e.target.value)} />
                <select className="ld-input" value={fOutcome} onChange={e => setFOutcome(e.target.value)}>
                  <option value="">Alle Stände</option>
                  {Object.entries(data.labels).map(([kk, v]) => <option key={kk} value={kk}>{v}</option>)}
                </select>
                <span style={{ alignSelf: 'center', fontSize: 12.5, color: C.muted, fontWeight: 600 }}>{filtered.length} von {leads.length}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ld-t">
                  <thead><tr>
                    <th>Eingegangen</th><th>Name</th><th>Telefon</th><th>Erreichbar</th><th>Situation</th><th>FS</th>
                    <th>Stand</th><th>Gerät</th><th>Sprache</th><th>Herkunft</th><th>Kampagne</th><th>Meta</th><th title="ChatGPT Ads">GPT</th><th>PD</th>
                  </tr></thead>
                  <tbody>
                    {filtered.length === 0 && <tr><td colSpan={14}><div className="ld-empty">{leads.length ? 'Keine Leads passen zu diesem Filter.' : 'Noch keine Leads eingegangen.'}</div></td></tr>}
                    {filtered.map(l => (
                      <tr key={l.booking_ref} onClick={() => setSelected(l)}>
                        <td style={{ whiteSpace: 'nowrap', color: C.muted }}>{fmtDate(l.created_at)}</td>
                        <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{l.vorname} {l.nachname}{l.ort && <div style={{ fontWeight: 500, fontSize: 11, color: C.faint }}>{l.ort}</div>}</td>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}><a href={`tel:${(l.telefon || '').replace(/[^0-9+]/g, '')}`} onClick={e => e.stopPropagation()} style={{ color: C.blue, fontWeight: 700, textDecoration: 'none' }}>{l.telefon}</a></td>
                        <td style={{ whiteSpace: 'nowrap' }}>{REACH[l.erreichbarkeit] || '–'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{l.foerderfaehig ? <span className="pill" style={{ background: C.greenBg, color: C.green }}>{ARBEIT[l.arbeitslos]}</span> : <span style={{ color: C.muted }}>{ARBEIT[l.arbeitslos] || '–'}</span>}</td>
                        <td>{l.fuehrerschein_b === 'ja' ? '🚗' : l.fuehrerschein_b === 'nein' ? '—' : '?'}</td>
                        <td><OutcomePill outcome={l.outcome} label={l.outcome_label} stage={l.pd_stage} /></td>
                        <td style={{ whiteSpace: 'nowrap' }}>{deviceIcon(l.device_type)} <span style={{ color: C.muted, fontSize: 12 }}>{l.browser || '–'}</span>{!!l.is_inapp && <span style={{ fontSize: 10, color: C.blue, fontWeight: 700, marginLeft: 4 }}>In-App</span>}</td>
                        <td className="mono" style={{ color: C.muted }}>{l.browser_lang || '–'}</td>
                        <td style={{ whiteSpace: 'nowrap', color: C.muted }}>{[l.geo_city, l.geo_country].filter(Boolean).join(', ') || '–'}</td>
                        <td style={{ maxWidth: 160 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.utm_source === 'direkt' ? <span style={{ color: C.faint }}>direkt</span> : (l.utm_campaign || l.utm_source || '–')}</div>
                          {l.utm_content && <div style={{ fontSize: 11, color: C.faint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.utm_content}</div>}
                        </td>
                        <td><StatusPill status={l.meta_status} title={l.meta_error || ''} /></td>
                        <td><StatusPill status={l.oai_status} title={l.oai_error || (l.oai_validate_only ? 'Nur validiert, nicht gezählt' : '')} /></td>
                        <td onClick={e => { if (l.pd_deal_url) e.stopPropagation(); }}>
                          {l.pd_deal_url ? <a href={l.pd_deal_url} target="_blank" rel="noopener noreferrer"><StatusPill status={l.pd_status} /></a> : <StatusPill status={l.pd_status} title={l.pd_error || ''} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {selected && (
        <>
          <div className="ld-scrim" onClick={() => setSelected(null)} />
          <div className="ld-drawer">
            <div className="ld-sec" style={{ position: 'sticky', top: 0, background: C.white, zIndex: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 19, fontWeight: 700 }}>{selected.vorname} {selected.nachname}</div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <OutcomePill outcome={selected.outcome} label={selected.outcome_label} stage={selected.pd_stage} />
                    {selected.pd_stage && <span className="pill" style={{ background: C.borderSm, color: C.muted }}>{selected.pd_stage}</span>}
                  </div>
                </div>
                <button className="ld-btn" onClick={() => setSelected(null)}>Schließen</button>
              </div>
              <a className="ld-btn primary" style={{ width: '100%', justifyContent: 'center', marginTop: 12, height: 46, fontSize: 16 }} href={`tel:${(selected.telefon || '').replace(/[^0-9+]/g, '')}`}>📞 {selected.telefon}</a>
              {selected.pd_deal_url && <a className="ld-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} href={selected.pd_deal_url} target="_blank" rel="noopener noreferrer">Deal in Pipedrive öffnen →</a>}
            </div>
            <div className="ld-sec"><h4>Kontakt</h4>
              <Pair k="E-Mail" v={selected.email} /><Pair k="Wohnort" v={selected.ort} /><Pair k="Erreichbar" v={REACH[selected.erreichbarkeit]} /><Pair k="Eingegangen" v={fmtDate(selected.created_at)} />
              <Pair k="Nächste Aktivität" v={selected.pd_next_activity} />
            </div>
            <div className="ld-sec"><h4>Qualifizierung</h4>
              <Pair k="Situation" v={ARBEIT[selected.arbeitslos]} />
              <Pair k="Führerschein B" v={selected.fuehrerschein_b === 'ja' ? 'Ja' : selected.fuehrerschein_b === 'nein' ? 'Nein' : null} />
              <Pair k="Qualifiziert" v={selected.qualified ? 'Ja — förderfähig und mobil' : 'Nein'} />
              <Pair k="Ausfüllzeit" v={fmtDuration(selected.time_to_submit)} />
              <Pair k="Einwilligung" v={selected.consent_at ? fmtDate(selected.consent_at) : null} />
            </div>
            <div className="ld-sec"><h4>Gerät &amp; Herkunft</h4>
              <Pair k="Gerät" v={selected.device_type ? `${deviceIcon(selected.device_type)} ${selected.device_type}` : null} />
              <Pair k="Betriebssystem" v={selected.os} />
              <Pair k="Browser" v={selected.browser ? `${selected.browser}${selected.is_inapp ? ' (In-App)' : ''}` : null} />
              <Pair k="Bildschirm" v={selected.screen_size} mono /><Pair k="Sprache" v={selected.browser_lang} mono /><Pair k="Zeitzone" v={selected.client_tz} mono />
              <Pair k="Ort (IP)" v={[selected.geo_city, selected.geo_region, selected.geo_country].filter(Boolean).join(', ')} />
            </div>
            <div className="ld-sec"><h4>Anzeige</h4>
              <Pair k="Quelle" v={selected.utm_source} mono /><Pair k="Medium" v={selected.utm_medium} mono /><Pair k="Platzierung" v={selected.placement} mono />
              <Pair k="Kampagne" v={selected.utm_campaign} mono /><Pair k="Anzeige" v={selected.utm_content} mono /><Pair k="Anzeigengruppe" v={selected.utm_term} mono />
              <Pair k="Kampagnen-ID" v={selected.fb_campaign_id} mono /><Pair k="Anzeigengr.-ID" v={selected.fb_adset_id} mono /><Pair k="Anzeigen-ID" v={selected.fb_ad_id} mono />
              <Pair k="Klick-ID" v={selected.fbclid ? 'vorhanden' : 'keine'} /><Pair k="Landingpage" v={selected.landing_url} /><Pair k="Referrer" v={selected.referrer_url} />
            </div>
            <div className="ld-sec"><h4>Übergabe</h4>
              <div className="ld-pair"><span className="k">Meta-Event</span><span className="v"><StatusPill status={selected.meta_status} /> {selected.meta_error && <span style={{ color: C.red, fontSize: 11 }}>{selected.meta_error}</span>}</span></div>
              <div className="ld-pair"><span className="k">ChatGPT Ads</span><span className="v"><StatusPill status={selected.oai_status} /> {selected.oai_validate_only && <span style={{ color: C.amber, fontSize: 11 }}>nur validiert</span>} {selected.oai_error && <span style={{ color: C.red, fontSize: 11 }}>{selected.oai_error}</span>}</span></div>
              <div className="ld-pair"><span className="k">Pipedrive</span><span className="v"><StatusPill status={selected.pd_status} />{selected.pd_reused && <span style={{ fontSize: 11, color: C.muted, marginLeft: 6 }}>(bestehender Deal)</span>}{selected.pd_error && <span style={{ color: C.red, fontSize: 11, marginLeft: 6 }}>{selected.pd_error}</span>}</span></div>
              <div className="ld-pair"><span className="k">E-Mails</span><span className="v"><StatusPill status={selected.mail_status} /></span></div>
              <Pair k="Meta Event-ID" v={selected.meta_event_id} mono />
              <Pair k="GPT Event-ID" v={selected.oai_event_id} mono />
              <Pair k="Referenz" v={selected.booking_ref} mono />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
